/**
 * A multi-channel (display + audio) report fixture, for the composite template.
 *
 * There is no multi-channel mockup — the three approved mockups are each single-channel — so this
 * fixture exists to exercise composition at the render layer: one document that carries an
 * `overall` rollup, a DISPLAY channel summary AND an AUDIO channel summary in `channels[]`, and an
 * `audio` side-car. It is built on the audio sample (which already carries a valid `overall`,
 * `audio`, `targeting`, `presentation` and `provenance`) with the `channels[]` array populated,
 * so the composite template's per-channel sections have their own figures to render.
 *
 * Numbers echo the pinned BHCCSUM26 shape (display 85,599 / 2,200; audio 121,192 / 6,621), where
 * the point is that each section shows ITS OWN figures — not that they match an issued PDF, which
 * for a layout this fixture defines rather than reproduces.
 */

import { SAMPLE_AUDIO_REPORT } from './sample-audio-report.js';
import type { ReportDataModel } from './report-data-model.js';

const base = SAMPLE_AUDIO_REPORT as unknown as ReportDataModel;

export const SAMPLE_COMPOSITE_REPORT = {
  ...base,
  report: { ...base.report, id: 'r-composite-1' },
  campaign: {
    ...base.campaign,
    reportSo: 'COMPOSITE1', so: 'COMPOSITE1',
    name: 'Multi-Channel Campaign', channels: ['DISPLAY', 'AUDIO'],
  },
  channels: [
    {
      channel: 'DISPLAY',
      delivery: {
        impressions: 85_599, retargetImpressions: 4_967, clicks: 2_200, retargetClicks: 0,
        mobileClicks: 1_400, desktopClicks: 800,
      },
      contract: { targetImpressions: 87_500 },
      derived: {
        ctr: 2.5143, ctrBasis: 'contracted-impressions',
        overDeliveredImpressions: 3_066, overDeliveryPct: 3.504,
      },
      devices: [
        { name: 'iPhone', clicks: 900 }, { name: 'Android', clicks: 600 }, { name: 'Other', clicks: 700 },
      ],
      browsers: [{ name: 'chrome', clicks: 1_200 }, { name: 'safari', clicks: 1_000 }],
    },
    {
      channel: 'AUDIO',
      delivery: {
        impressions: 121_192, retargetImpressions: 0, clicks: 6_621, retargetClicks: 0,
        mobileClicks: 0, desktopClicks: 0,
      },
      contract: { targetImpressions: 350_000 },
      derived: {
        ctr: 1.7459, ctrBasis: 'streams', overDeliveredImpressions: 0, overDeliveryPct: 0,
      },
      devices: [],
      browsers: [],
    },
  ],
} as unknown as ReportDataModel;
